import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text, FlexboxGrid, Row, Col, Form, Grid, FormInstance } from 'rsuite';
import AnnotatedCheckbox from '../../../components/form/AnnotatedCheckbox';
import FormTextField from '../../../components/form/FormTextField';
import {
  checkedValidation,
  requiredValidation,
} from '../../../common/validation';
import dayjs from 'dayjs';
import { isNullOrUndefined } from '../../../common/utils';
import {
  FLOWS_ENUM,
  RENTAL_STRUCTURE_DISPLAY_NAME_DICT,
  RENTAL_STRUCTURE_STATUS_ENUM,
} from '../../../common/enums';
import IconIndicator from '../../../components/user-feedback/IconIndicator';
import { C_DEFAULT_UPDATE_CERTIFICATION } from '../../../common/consts';
import Notice from '../../../components/user-feedback/Notice';
import FlowPageLayout from '../../layout/FlowLayout';
import { ApplicationData } from 'src/types/flow';
import { Documents } from './Documents';
import { SupplementalQuestions } from './SupplementalQuestions';
import { People } from './People';
import { type CheckType } from 'schema-typed';
import { FormikErrors, FormikValues } from 'formik';
import { RegistrationFlow, RequiredHiddable } from 'src/types/configuration';

interface ConfirmationPageProps {
  config: RegistrationFlow;
  customNotice?: React.ReactNode;
  data: ApplicationData;
  documentUploadErrors: string[];
  flow: FLOWS_ENUM;
  flowError: string;
  instructionsText?: string;
  loading?: boolean;
  localContactVerbiage: string;
  onConfirmation: (data: ApplicationData) => void;
  onGoBack?: () => void;
  parcelVerbiage: string;
  permitTypeVerbiage: string;
  rentalStructureRequiredStatus?: RequiredHiddable;
}
type FormModel = FormikValues & {
  signature: string;
  date: string;
  [key: string]: boolean | string | undefined;
};

type FormError = FormikErrors<FormModel>;
type FormValidationModel = {
  signature: CheckType<string, string>;
  [key: string]: CheckType<boolean, string> | CheckType<string, string>;
};

export const ConfirmationPage = ({
  config,
  customNotice = <></>,
  data,
  documentUploadErrors,
  flow,
  flowError,
  instructionsText = 'Please confirm the following information and sign below.',
  loading,
  localContactVerbiage,
  onConfirmation,
  onGoBack,
  parcelVerbiage,
  permitTypeVerbiage,
  rentalStructureRequiredStatus,
}: ConfirmationPageProps) => {
  const form = useRef<FormInstance>(null);

  const [formValues, setFormValues] = useState<FormModel>();
  const [formError, setFormError] = useState<FormError>({});
  const [isValidating, setIsValidating] = useState(false);

  const selectedPropertyDetailsQuestions = config.questions ?? [];
  const selectedDocuments = config.documents ?? [];

  const workInProgress = useMemo(() => {
    return isValidating || loading;
  }, [isValidating, loading]);

  const displayErrorMessageText = useMemo((): string => {
    if (flowError) {
      return flowError;
    } else if (
      !isNullOrUndefined(documentUploadErrors) &&
      documentUploadErrors.length > 0
    ) {
      return documentUploadErrors.join('\n');
    }
    return '';
  }, [flowError, documentUploadErrors]);

  const displayCertifications = useMemo(() => {
    const certifications = config.certifications ?? [];
    if (certifications.length > 0) {
      return certifications;
    }
    return [C_DEFAULT_UPDATE_CERTIFICATION];
  }, [config.certifications]);

  const formModel = useMemo((): FormModel => {
    const certificationMap: Record<string, boolean> = {};
    if (displayCertifications) {
      displayCertifications?.forEach(
        (_, idx) => (certificationMap[`certification_${idx}`] = false)
      );
    }
    return {
      signature: '',
      date: '',
      ...certificationMap,
    };
  }, [displayCertifications]);

  const validationModel = useMemo((): FormValidationModel => {
    const certificationValidationMap: Record<string, unknown> = {};
    if (displayCertifications) {
      displayCertifications?.forEach(
        (_, idx) =>
          (certificationValidationMap[`certification_${idx}`] =
            checkedValidation)
      );
    }
    return {
      signature: requiredValidation,
      ...certificationValidationMap,
    };
  }, [displayCertifications]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  const validateCertifications = () => {
    if (!form.current?.check()) return false;
    return true;
  };

  const prepareMandatoryAcknowledgementsObject = (): Record<
    string,
    { label: string; value: boolean }
  > => {
    return displayCertifications.reduce(
      (
        acc: Record<string, { label: string; value: boolean }>,
        certification,
        idx
      ) => {
        acc[`aff_${idx + 1}`] = {
          label: certification,
          value: !!formValues?.[`certification_${idx}`],
        };
        return acc;
      },
      {}
    );
  };

  const dispatchConfirmation = () => {
    if (loading) return;
    setIsValidating(true);
    if (!validateCertifications()) {
      setIsValidating(false);
      return;
    }
    setIsValidating(false);
    const mandatoryAcknowledgements = prepareMandatoryAcknowledgementsObject();
    onConfirmation({
      certifications: {
        mandatoryAcknowledgements: { ...mandatoryAcknowledgements },
        signatory: formValues?.signature as string,
      },
    });
  };

  const handleNextClick = () => {
    dispatchConfirmation();
  };

  return (
    <FlowPageLayout
      flow={flow}
      title="Confirmation"
      onNext={handleNextClick}
      onBack={onGoBack}
      showBack={!!onGoBack}
      omitNavigationForward
      showNext
    >
      <Form
        className="mt-12"
        ref={form}
        checkTrigger="blur"
        formDefaultValue={formModel}
        onCheck={setFormError}
        onChange={(f) => {
          setFormValues({ ...formModel, ...(f as Record<string, boolean>) });
        }}
        fluid
      >
        <FlexboxGrid className="mt-12 ml-0">
          <FlexboxGrid.Item colspan={24}>
            <div role="grid">
              <Row>
                <Col xs={24}>
                  <Text>{instructionsText}</Text>
                </Col>
              </Row>
            </div>
            {customNotice}
          </FlexboxGrid.Item>
          <FlexboxGrid.Item
            colspan={24}
            className="mt-24"
          >
            <div role="grid">
              <Row style={{ minHeight: '60px' }}>
                <Col xs={24}>
                  <Grid fluid>
                    <Row className="mb-8">
                      <Col
                        xs={24}
                        lg={12}
                      >
                        <Text className="text-bold">Property Address</Text>
                        <Text className="mb-8">
                          {data.property?.propertyaddress}
                        </Text>
                        <Text className="text-bold">{parcelVerbiage}</Text>
                        <Text className="mb-8">
                          {data?.property?.propertyid}
                        </Text>
                      </Col>
                      <Col
                        xs={24}
                        lg={12}
                      >
                        {data?.property?.unitnumber && (
                          <>
                            <Text className="text-bold">Unit Number</Text>
                            <Text className="mb-8">
                              {data?.property?.unitnumber}
                            </Text>
                          </>
                        )}
                        {rentalStructureRequiredStatus !==
                          RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && (
                          <>
                            <Text className="text-bold">Rental Structure</Text>
                            <Text className="mb-8">
                              {(data?.property?.rentalstructure &&
                                RENTAL_STRUCTURE_DISPLAY_NAME_DICT[
                                  data?.property?.rentalstructure
                                ]) ||
                                'No option selected'}
                            </Text>
                          </>
                        )}
                      </Col>
                    </Row>
                    <People
                      permitVerbiage={permitTypeVerbiage}
                      localContactVerbiage={localContactVerbiage}
                      data={data}
                    />
                    <Documents
                      documents={data?.documents}
                      configDocuments={selectedDocuments}
                    />
                    {data?.propertyDetails?.supplementalQuestions && (
                      <SupplementalQuestions
                        questions={data?.propertyDetails?.supplementalQuestions}
                        configQuestions={selectedPropertyDetailsQuestions}
                      />
                    )}
                    <Row>
                      <Col xs={24}>
                        <h2>Certifications</h2>
                        {displayCertifications?.map((certificaton, idx) => {
                          return (
                            <AnnotatedCheckbox
                              style={{
                                marginTop: 0,
                                marginBottom: '16px',
                              }}
                              key={`certification_${idx}`}
                              label={certificaton}
                              hasError={!!formError?.[`certification_${idx}`]}
                              name={`certification_${idx}`}
                              validationRule={
                                validationModel?.[
                                  `certification_${idx}`
                                ] as CheckType<boolean, string>
                              }
                            />
                          );
                        })}
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24}>
                        <h2>Electronic Signature</h2>
                      </Col>
                    </Row>
                    <Row
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                      }}
                    >
                      <Col xs={12}>
                        <FormTextField
                          label="Legal Name of Authorized Signatory"
                          validationRule={validationModel?.signature}
                          hasError={!!formError['signature'] as boolean}
                          name="signature"
                          errorMessage="Signature is required"
                        />
                      </Col>
                      <Col xs={12}>
                        <FormTextField
                          value={dayjs().format('MM/DD/YYYY')}
                          label="Date"
                          name="date"
                          disabled
                        />
                      </Col>
                    </Row>
                    {workInProgress && (
                      <Row className="mv-16">
                        <Col xs={24}>
                          <IconIndicator />
                        </Col>
                      </Row>
                    )}
                    {!workInProgress && !!displayErrorMessageText && (
                      <Row className="mv-16">
                        <Col xs={24}>
                          <Notice message={displayErrorMessageText} />
                        </Col>
                      </Row>
                    )}
                  </Grid>
                </Col>
              </Row>
            </div>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Form>
    </FlowPageLayout>
  );
};
